import { Injectable, signal, Signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { ApiCacheService } from './api-cache.service';
import { CacheResponseSource } from './api-cache.models';

export interface RevalidatedResource<T> {
  /** Dado atual — cache imediatamente, depois atualizado se a API mandar 200. */
  data: Signal<T | null>;
  /**
   * true só até a primeira emissão real acontecer (cache OU rede) — não fica
   * true de novo em revalidações silenciosas de background.
   */
  loading: Signal<boolean>;
  error: Signal<unknown | null>;
  source: Signal<CacheResponseSource | null>;
}

export interface RevalidatedGetOptions {
  params?: HttpParams | Record<string, string | number | boolean>;
}

type HttpOptions = { headers: HttpHeaders; withCredentials?: boolean };

// Substitui HttpClient.get() direto pra rotas GET que já têm ETag no backend
// (ver HandleEtag middleware). Cache-first: se já tem algo salvo (memória ou
// sessionStorage), mostra na hora — sem skeleton — e revalida em segundo
// plano com If-None-Match. Um 304 não altera a tela; um 200 atualiza os
// signals, o que já dispara o re-render de quem lê esse valor no template.
@Injectable({
  providedIn: 'root'
})
export class RevalidatedHttpService {
  constructor(
    private readonly http: HttpClient,
    private readonly cache: ApiCacheService
  ) {}

  getSignal<T>(
    cacheKey: string,
    url: string,
    httpOptionsPromise: Promise<HttpOptions>,
    options: RevalidatedGetOptions = {}
  ): RevalidatedResource<T> {
    const cached = this.cache.get<T>(cacheKey);

    const data = signal<T | null>(cached?.data ?? null);
    // Só true quando NÃO existe cache — é isso que evita mostrar skeleton de
    // novo numa rota que já tinha dado salvo, mesmo enquanto a revalidação em
    // segundo plano ainda está em voo.
    const loading = signal<boolean>(!cached);
    const error = signal<unknown | null>(null);
    const source = signal<CacheResponseSource | null>(cached ? 'cache' : null);

    httpOptionsPromise.then(baseOptions => {
      let headers = baseOptions.headers;
      if (cached?.etag) {
        headers = headers.set('If-None-Match', cached.etag);
      }

      this.http.get<T>(url, {
        ...baseOptions,
        headers,
        params: options.params as HttpParams,
        observe: 'response'
      }).subscribe({
        next: response => {
          const body = response.body;

          // HttpClient só chega aqui em 2xx — um 200 sempre tem body; se vier
          // null é sinal de resposta malformada, mas não vale travar o cache
          // que já estava exibido.
          if (body !== null && body !== undefined) {
            const etag = response.headers.get('ETag');
            this.cache.set(cacheKey, body, etag);
            data.set(body);
            source.set('network');
          }

          loading.set(false);
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse && err.status === 304 && cached) {
            // Cache confirmado válido — não mexe em data/source, só destrava loading.
            loading.set(false);
            return;
          }

          // 400/401/403/404/500 etc — propaga pro chamador tratar como sempre.
          // Dado cacheado (se houver) permanece na tela, não é apagado aqui.
          error.set(err);
          loading.set(false);
        }
      });
    });

    return {
      data: data.asReadonly(),
      loading: loading.asReadonly(),
      error: error.asReadonly(),
      source: source.asReadonly()
    };
  }
}
